import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MagnifyingGlass,
  Plus,
  DownloadSimple,
  UploadSimple,
  CalendarBlank,
  Funnel,
  ArrowsClockwise,
  Trash,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const STATUS_COLORS = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Completed: "bg-blue-50 text-blue-700 border-blue-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

const PAYMENT_COLORS = {
  Unpaid: "bg-red-50 text-red-600 border-red-200",
  Partial: "bg-amber-50 text-amber-600 border-amber-200",
  Paid: "bg-emerald-50 text-emerald-600 border-emerald-200",
  Refunded: "bg-gray-100 text-gray-600 border-gray-200",
};

const ReservationsPage = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchReservations();
  }, [filterStatus, filterPayment]);

  const fetchReservations = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatus) params.append("booking_status", filterStatus);
      if (filterPayment) params.append("payment_status", filterPayment);
      const res = await api.get(`/reservations?${params}`);
      setReservations(res.data);
    } catch (err) {
      toast.error("Failed to load reservations");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReservations();
  };

  const handleExport = async (format) => {
    try {
      const res = await api.get(`/reservations-export/${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reservations_export.${format === "excel" ? "xlsx" : "csv"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
      const endpoint = isExcel ? "/reservations-import/excel" : "/reservations-import/csv";
      const res = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${res.data.imported} reservations${res.data.total_rows ? ` from ${res.data.total_rows} rows` : ""}`);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      fetchReservations();
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this reservation?")) return;
    try {
      await api.delete(`/reservations/${id}`);
      setReservations((prev) => prev.filter((r) => r.id !== id));
      toast.success("Reservation deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Reservations</p>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-navy">Booking Management</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => handleExport("csv")} data-testid="export-csv-btn">
            <DownloadSimple size={14} /> CSV
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => handleExport("excel")} data-testid="export-excel-btn">
            <DownloadSimple size={14} /> Excel
          </Button>
          <label className="cursor-pointer">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 pointer-events-none" disabled={importing} data-testid="import-csv-btn">
              <UploadSimple size={14} /> {importing ? "Importing..." : "Import"}
            </Button>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleImport} />
          </label>
          <Button onClick={() => navigate("/reservations/new")} className="bg-navy hover:bg-[#2a3a5e] text-white gap-1.5 text-xs h-8" data-testid="new-reservation-btn">
            <Plus size={14} /> <span className="hidden sm:inline">New</span> Reservation
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white border border-border p-4 mb-4">
        <form onSubmit={handleSearch} className="flex gap-2 items-center">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client, reservation ID, trip..."
              className="pl-9 bg-cream/50 border-border text-sm h-9"
              data-testid="reservation-search"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9">Search</Button>
          <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => setShowFilters(!showFilters)}>
            <Funnel size={16} />
          </Button>
        </form>
        {showFilters && (
          <div className="flex gap-3 mt-3 pt-3 border-t border-border flex-wrap">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-xs border border-border px-3 py-1.5 bg-white"
              data-testid="filter-booking-status"
            >
              <option value="">All Booking Status</option>
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <select
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
              className="text-xs border border-border px-3 py-1.5 bg-white"
              data-testid="filter-payment-status"
            >
              <option value="">All Payment Status</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Paid</option>
              <option value="Refunded">Refunded</option>
            </select>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => { setFilterStatus(""); setFilterPayment(""); }}>Clear Filters</Button>
          </div>
        )}
      </div>

      {/* Reservations List */}
      <div className="bg-white border border-border">
        {reservations.length === 0 ? (
          <div className="p-12 text-center">
            <ArrowsClockwise size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">No reservations yet</p>
            <p className="text-sm text-muted-foreground mb-4">Convert a proposal to a reservation, or create one manually</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {reservations.map((r) => (
              <div
                key={r.id}
                className="p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between hover:bg-cream/30 cursor-pointer transition-colors gap-3"
                onClick={() => navigate(`/reservations/${r.id}/edit`)}
                data-testid={`reservation-row-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-navy font-semibold">{r.reservation_id}</span>
                    {r.proposal_reference && (
                      <span className="text-[10px] text-muted-foreground">from {r.proposal_reference}</span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 border ${STATUS_COLORS[r.booking_status] || "bg-gray-100 text-gray-600"}`}>
                      {r.booking_status}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 border ${PAYMENT_COLORS[r.payment_status] || "bg-gray-100 text-gray-600"}`}>
                      {r.payment_status}
                    </span>
                  </div>
                  <p className="font-medium text-navy text-sm truncate">{r.client_name || "No Client"}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.trip_title || "Untitled"}</p>
                  {r.hotels?.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {r.hotels.map((h) => h.hotel_name).filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-serif text-lg text-gold">{formatCurrency(r.total_sale)}</p>
                    <p className="text-xs text-emerald-600">Margin: {formatCurrency(r.total_margin)}</p>
                    <p className="text-[10px] text-muted-foreground flex items-center justify-end gap-1 mt-0.5">
                      <CalendarBlank size={10} />
                      {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy") : ""}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-red-600 h-8 w-8 p-0"
                    onClick={(e) => handleDelete(r.id, e)}
                    data-testid={`delete-reservation-${r.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationsPage;
